import { useMemo } from "react";
import { UIOrder, UIUnifiedPriceBreakdownItem } from "@flights/types";
import { useI18n, t } from "@bookingcom/lingojs-react";
import { localizationUtils, ryanairUtils } from "@flights/web-api-utils-universal";
import { useOrderWithDiscount } from "hooks/useOrderWithDiscount";
import { transformDisclaimersForPriceBreakdown } from "./transforms/disclaimers";
import uniq from "lodash/uniq";
import { BreakdownProps } from "./Breakdown/Breakdown";
import useRouteName from "hooks/useRouteName";

export type UnifiedPriceBreakdownPostBookingControllerProps = {
  order?: UIOrder;
};

export function useUnifiedPriceBreakdownPostBookingController(props: UnifiedPriceBreakdownPostBookingControllerProps) {
  const { order } = props;
  const i18n = useI18n();
  const routeName = useRouteName();
  const unifiedPriceBreakdown = useMemo(() => order?.unifiedPriceBreakdown, [order?.unifiedPriceBreakdown]);
  const segments = useMemo(() => order?.airOrder?.flightSegments, [order?.airOrder?.flightSegments]);
  const instalmentsFees = order?.totalPrice?.instalmentsFees;

  const isHungaryDepartingRyanAir = useMemo(
    () => localizationUtils.isHungaryDepartingRyanAirFlight(segments),
    [segments]
  );

  const addedItems: UIUnifiedPriceBreakdownItem[] = useMemo(() => {
    if (!instalmentsFees) return [];
    const installmentItem: UIUnifiedPriceBreakdownItem = {
      id: "dynamic-instalments-fees",
      scope: "FEE",
      title: i18n.trans(t("flights_price_breakdown_instalment_label")),
      price: instalmentsFees,
      items: []
    };
    return [installmentItem];
  }, [i18n, instalmentsFees]);

  const disclaimers = useMemo(() => {
    return uniq(transformDisclaimersForPriceBreakdown(order?.totalPrice));
  }, [order]);

  const { isWithDiscount } = useOrderWithDiscount(order?.airOrder); // flights_unified_price_breakdown_pb

  const isRyanair = useMemo(() => ryanairUtils.isRyanairSegments(segments), [segments]);

  const expandedByDefault = useMemo<BreakdownProps["expandedByDefault"]>(() => {
    return isRyanair
      ? {
          FLIGHT_ADULT: true,
          FLIGHT_CHILD: true,
          FLIGHT_INFANT: true,
          EXTRA: true
        }
      : undefined;
  }, [isRyanair]);

  return {
    unifiedPriceBreakdown,
    isHungaryDepartingRyanAir,
    expandedByDefault,
    disclaimers,
    isWithDiscount, // flights_unified_price_breakdown_pb
    addedItems,
    routeName
  };
}
